import { useState, useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Download, RefreshCw, Mail, Calendar, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

interface WaitlistEntry {
  id: string;
  email: string;
  created_at: string;
  source: string | null;
}

export default function AdminWaitlist() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const content = {
    en: {
      title: 'Waitlist Dashboard',
      subtitle: 'View and manage Gift Store waitlist signups',
      totalSignups: 'Total Signups',
      exportCsv: 'Export CSV',
      refresh: 'Refresh',
      email: 'Email',
      signedUp: 'Signed Up',
      source: 'Source',
      noEntries: 'No waitlist signups yet',
      noEntriesDesc: 'Signups will appear here when users join the waitlist.',
      accessDenied: 'Access Denied',
      accessDeniedDesc: 'You must be an admin to view this page.',
      exportSuccess: 'CSV exported successfully',
      refreshSuccess: 'Data refreshed',
    },
    es: {
      title: 'Panel de Lista de Espera',
      subtitle: 'Ver y gestionar inscripciones de la Tienda de Regalos',
      totalSignups: 'Total de Inscripciones',
      exportCsv: 'Exportar CSV',
      refresh: 'Actualizar',
      email: 'Email',
      signedUp: 'Inscrito',
      source: 'Fuente',
      noEntries: 'Aún no hay inscripciones',
      noEntriesDesc: 'Las inscripciones aparecerán aquí cuando los usuarios se unan.',
      accessDenied: 'Acceso Denegado',
      accessDeniedDesc: 'Debes ser administrador para ver esta página.',
      exportSuccess: 'CSV exportado exitosamente',
      refreshSuccess: 'Datos actualizados',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchWaitlist();
    }
  }, [isAdmin]);

  const checkAdminStatus = async () => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin',
      });

      if (error) throw error;
      setIsAdmin(data);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchWaitlist();
    setIsRefreshing(false);
    toast.success(t.refreshSuccess);
  };

  const handleExportCsv = () => {
    if (entries.length === 0) return;

    const headers = ['Email', 'Signed Up', 'Source'];
    const csvContent = [
      headers.join(','),
      ...entries.map((entry) =>
        [
          entry.email,
          format(new Date(entry.created_at), 'yyyy-MM-dd HH:mm:ss'),
          entry.source || 'N/A',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `waitlist-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success(t.exportSuccess);
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-20">
          <Card className="max-w-md text-center">
            <CardHeader>
              <Shield className="h-12 w-12 text-destructive mx-auto mb-4" />
              <CardTitle>{t.accessDenied}</CardTitle>
              <CardDescription>{t.accessDeniedDesc}</CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 py-12">
        <div className="container max-w-5xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">{t.title}</h1>
            <p className="text-muted-foreground">{t.subtitle}</p>
          </div>

          {/* Stats Card */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.totalSignups}</p>
                    <p className="text-3xl font-bold text-foreground">
                      {isLoading ? <Skeleton className="h-9 w-16" /> : entries.length}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    {t.refresh}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={entries.length === 0}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {t.exportCsv}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Entries Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {({ en: 'Waitlist Entries', es: 'Inscripciones', fr: 'Inscriptions', pt: 'Inscrições', ar: 'قائمة الانتظار', ja: '待機リスト', ko: '대기자 목록', id: 'Daftar Tunggu' }[language]) || 'Waitlist Entries'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">{t.noEntries}</h3>
                  <p className="text-sm text-muted-foreground">{t.noEntriesDesc}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.email}</TableHead>
                      <TableHead>{t.signedUp}</TableHead>
                      <TableHead>{t.source}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">{entry.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(entry.created_at), 'MMM d, yyyy')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{entry.source || 'unknown'}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
